import { useLazyQuery } from '@apollo/client/react';
import { merge, omit } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { QueryGetCandidateSuggestionsModalJobsArgs, SuggestionModalJobsDto } from '@pflegia/data-access';
import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { DeclineFeedback, SuggestionsTrigger } from '@pflegia/types/common/enums';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { SuggestionModalStoreData, useSuggestionsModalStore } from '@pflegia/ui/stores/suggestion-modal.store';

import { useFiltersState } from '@domains/job-board/application/use-filter-state.hook';
import { filterValuesToGuestFilters } from '@domains/job-board/utils/filter-values-to-guest-filters';

import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useTracking } from '@shared/application/tracking.service';
import { GET_CANDIDATE_SUGGESTIONS_MODAL_JOBS } from '@shared/graphql/operations/candidate-suggestions-modal-jobs';

export const useSuggestionModalLogic = () => {
  const { t } = useTranslation('job', { keyPrefix: 'suggestionsModal' });
  const { push } = useRouter();
  const { track } = useTracking();

  const suggestionStore = useSuggestionsModalStore();
  const { onClose, onOpen, set: setSuggestionsStoreData } = suggestionStore;

  const [getSuggestedPositions] = useLazyQuery<{ data: SuggestionModalJobsDto }, QueryGetCandidateSuggestionsModalJobsArgs>(
    GET_CANDIDATE_SUGGESTIONS_MODAL_JOBS,
    { fetchPolicy: 'no-cache' },
  );
  const [loadCandidate] = useLazyQuery(CandidateDocument);

  const authenticated = useAuth();
  const filterState = useFiltersState({ keepFilters: true });
  const queryVariableFilters = useMemo(() => omit(filterValuesToGuestFilters(filterState.filters), ['jobTitleIds']), [filterState.filters]);

  const handleCloseWithRedirect = useCallback(
    (nextRoute?: string, withTrackingEvent = true) => {
      if (withTrackingEvent && !suggestionStore.hadInteraction) {
        track(TrackingEventName.SuggestionsModalDismissed);
      }
      onClose();
      if (nextRoute ?? suggestionStore.initialNavigation) {
        push((nextRoute ?? suggestionStore.initialNavigation) as string);
      }
    },
    [onClose, push, suggestionStore.hadInteraction, suggestionStore.initialNavigation, track],
  );

  const getModalTitle = useCallback(
    (payload: Partial<SuggestionModalStoreData>): string | undefined => {
      const feedBackItems = payload.feedbackItems ?? [];
      if (feedBackItems.length === 1 && feedBackItems[0] === DeclineFeedback.Others) {
        return t(`title.${SuggestionsTrigger.DeclineWithoutReason}`);
      }

      const translations = feedBackItems.filter(item => item !== DeclineFeedback.Others).map(item => t(`feedback.${item}`));
      const reason =
        translations.length > 2
          ? `${translations.slice(0, translations.length - 1).join(', ')} und ${translations[translations.length - 1]}`
          : translations.join(' und ');

      const trigger =
        payload.trigger || (feedBackItems.length > 0 ? SuggestionsTrigger.DeclineWithReason : SuggestionsTrigger.DeclineWithoutReason);
      return t(`title.${trigger}`, { reason });
    },
    [t],
  );

  const initializeModalDisplay = useCallback(
    async (payload: SuggestionModalStoreData) => {
      // Check for candidate state
      if (!authenticated) return false;
      const { data: candidateQueryData } = await loadCandidate();
      if (!candidateQueryData?.candidate.isWithinSuggestionsModalDisplayLimit) return false;

      const { feedbackItems, jobPositionId } = payload;
      setSuggestionsStoreData('queryResult', { loading: true });
      // The usage of onCompleted and onError did not work with the current approach. Hence the try/catch
      try {
        const { data } = await getSuggestedPositions({
          variables: {
            filters: queryVariableFilters,
            suggestionsModalInput: { feedbackItems, jobPositionId },
          },
        });
        setSuggestionsStoreData('queryResult', { data: data?.data, loading: false });
        return data?.data?.canDisplayModal;
      } catch (error) {
        setSuggestionsStoreData('queryResult', { error, loading: false });
      }

      return false;
    },
    [authenticated, getSuggestedPositions, loadCandidate, queryVariableFilters, setSuggestionsStoreData],
  );

  const handleOpen = useCallback(
    async (triggeringPayload?: Partial<SuggestionModalStoreData>, handleCloseOnOpenPrevented?: boolean | (() => void)) => {
      const payload = merge({}, suggestionStore, triggeringPayload);
      if (payload.feedbackItems?.includes(DeclineFeedback.NotLookingForJob)) {
        handleCloseWithRedirect(triggeringPayload?.initialNavigation, false);
        return;
      }
      const isDisplayAllowed = await initializeModalDisplay(payload);
      if (!isDisplayAllowed) {
        if (handleCloseOnOpenPrevented) {
          if (typeof handleCloseOnOpenPrevented === 'function') {
            handleCloseOnOpenPrevented();
          }
          handleCloseWithRedirect(triggeringPayload?.initialNavigation, false);
        }
        return;
      }

      onOpen({
        ...payload,
        title: getModalTitle(payload),
      });
    },
    [getModalTitle, handleCloseWithRedirect, onOpen, initializeModalDisplay, suggestionStore],
  );

  const prepareSuggestionsStoreData = useCallback(
    (payload: Partial<SuggestionModalStoreData>) => {
      Object.entries(payload).forEach(([key, value]) => {
        // @ts-expect-error suggestionStore is Partial<SuggestionModalStoreData>
        setSuggestionsStoreData(key, value);
      });
      let { trigger } = payload;
      if (trigger === SuggestionsTrigger.DeclineWithReason && !payload.feedbackItems?.length) {
        trigger = SuggestionsTrigger.DeclineWithoutReason;
        setSuggestionsStoreData('trigger', trigger);
      } else if (trigger === SuggestionsTrigger.DeclineWithoutReason && payload.feedbackItems?.length) {
        trigger = SuggestionsTrigger.DeclineWithReason;
        setSuggestionsStoreData('trigger', trigger);
      }
      if (!suggestionStore.showAfterModalClose) {
        setSuggestionsStoreData('showAfterModalClose', true);
      }
    },
    [setSuggestionsStoreData, suggestionStore.showAfterModalClose],
  );

  return {
    handleOpen,
    handleClose: handleCloseWithRedirect,
    setSuggestionsStoreData,
    prepareSuggestionsStoreData,
    store: suggestionStore,
  };
};
